import classnames from "classnames";
import React from "react";
import { connect } from "react-redux";
import { CustomScroller, LinkButton, Loader } from "../../../../core/atoms/atoms";
import { SearchComponent } from "../../../../core/atoms/rrf/searchComponent";
import { api } from "../../../../shared/api/system/apiClient";
import { LocationCard } from "@travellocal/ui";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { PageScrollPositionHelper } from "../../../../../../packages/ui/src/utils/pageScrollPositionHelper";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import { isClickInside, ResponsiveBreakpoints } from "../../../../../../packages/ui/src/utils/uiHelpers";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./immersiveSearch.scss";

interface OwnProps {
  className?: string;
}

interface StateProps {
  regionsState: IState<IRegionSummary[]>;
}

type Props = OwnProps & StateProps;

type LocalState = Partial<{
  searchActive: boolean;
}>;

class ImmersiveSearchComponent extends React.Component<Props, LocalState> {
  private rootRef: HTMLDivElement;
  private searchComponentRef: HTMLDivElement;
  private resultsWrapRef: HTMLDivElement;

  public constructor(props: Props) {
    super(props);
    this.state = {};
  }

  private setRootRef = (ref: HTMLDivElement) => this.rootRef = ref;
  private setSearchComponentRef = (ref: HTMLDivElement) => this.searchComponentRef = ref;
  private setResultsWrapRef = (ref: HTMLDivElement) => this.resultsWrapRef = ref;

  /**
   * Override default close on click outside behaviour of search component
   */
  private isClickInside = (e: MouseEvent) => {
    if (!this.rootRef || !this.resultsWrapRef) {
      return false;
    }

    // TODO not sure if ref works with CustomScroller - grab via DOM
    const scrollerRect = this.resultsWrapRef.getElementsByTagName("div")[0].getBoundingClientRect();
    const searchRect = this.searchComponentRef.getBoundingClientRect();
    const rootRect = this.rootRef.getBoundingClientRect();

    return isClickInside(e, scrollerRect) || isClickInside(e, searchRect) || isClickInside(e, rootRect);
  }

  /**
   * When the search input is clicked or tapped
   */
  private onSearchFocus = () => {
    if (window.innerWidth < ResponsiveBreakpoints.tablet) {
      // Hide iOS address bar. We don't use lockPageScroll, because there's limited input area.
      PageScrollPositionHelper.storeScrollPosition();
      window.scrollTo(0, 1);
    }

    this.setState({ searchActive: true });
  }

  /**
   * When clicked out of component, or when search results closed
   */
  private closeSearchResults = () => {
    PageScrollPositionHelper.restoreScrollPosition();
    this.setState({ searchActive: false });
  }

  private getSearchResults = async (query?: string) => {
    const params = {
      query,
      locationType: query ? [LocationType.Country, LocationType.Region] : LocationType.Country, // Default to countries
      pageSize: 100, // Show all countries in default interaction
    };

    const results = await api.getLocations(params);
    return results.json.payload;
  }

  /**
   * Render a single item returned from the API.
   */
  private renderSearchResult = (item: ILocationSummary, onSelect: () => void) => {
    // Don't pass down the onSelect handler - let Link take over
    return (
      <LocationCard
        location={item}
        onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "Homepage search")}
      />
    );
  }

  private renderSearchError = (message: string) => {
    return (
      <div className="search-result-message">
        <h3>Sorry!</h3>
        <p>{message}</p>
      </div>
    );
  }

  public render() {
    const { className, regionsState } = this.props;
    const { searchActive } = this.state;

    const rootClassName = classnames(
      "immersive-search",
      "columns is-desktop column is-paddingless is-10-widescreen is-offset-1-widescreen", // Bulma
      { "is-active": searchActive }, // Show/ hide search results
      className,
    );

    return (
      <div ref={this.setRootRef} className={rootClassName}>
        <SearchComponent<ILocationSummary>
          setRootRef={this.setSearchComponentRef}
          className="column"
          getItemName={x => x.name}
          getItemId={x => x.id}
          getResults={this.getSearchResults}
          getBackupResults={this.getSearchResults}
          noResultsMessage={() => `We don't currently serve that destination. Take a look at our other destinations to get some inspiration!`}
          onFocus={this.onSearchFocus}
          onBlur={this.closeSearchResults}
          isClickInside={this.isClickInside}
          inputProps={{
            placeholder: "Where do you want to go?",
          }}
          resultsProps={{
            className: "immersive-search-results",
          }}
          renderResultItem={this.renderSearchResult}
          renderErrorItem={this.renderSearchError}
          renderResultsContainer={resultsList => (
            <div className="immersive-search-results-wrap" ref={this.setResultsWrapRef}>
              {resultsList && <CustomScroller>{resultsList}</CustomScroller>}
            </div>
          )}
        />

        <div className="region-links column is-8-desktop columns is-vcentered is-marginless">
          <div className="column is-3">
            <p className="heading--3 is-marginless">Browse by region</p>
          </div>
          <div className="column is-vcentered columns is-multiline">
            {MatchState(regionsState, {
              none: err => err
                ? <LinkButton to={WebRoutes.destinations()}>All destinations</LinkButton>
                : <Loader />,
              data: regions => regions.map(region => (
                <LinkButton
                  key={region.id}
                  to={WebRoutes.region(region.slug)}
                  buttonClass="secondary-button"
                  className="text--regular text--dark"
                >
                  {region.name}
                </LinkButton>
              )),
            })}
          </div>
        </div>
      </div>
    );
  }
}

const stateToProps = (state: IWebStoreState, props: OwnProps): StateProps => ({
  regionsState: state.regions,
});

export const ImmersiveSearch = connect(stateToProps)(ImmersiveSearchComponent);
